import { nullableCoordinates } from "@/schemas/coordinates";
import { z } from "@/zod";

export type Coordinates = z.infer<typeof nullableCoordinates>;

export enum GeolocationError {
  PERMISSION_DENIED = "PERMISSION_DENIED",
  POSITION_UNAVAILABLE = "POSITION_UNAVAILABLE",
  TIMEOUT = "TIMEOUT",
  NOT_SUPPORTED = "NOT_SUPPORTED",
}

export enum GeolocationPermissionState {
  GRANTED = "granted",
  DENIED = "denied",
  PROMPT = "prompt",
}

export enum GeolocationAccuracyLevel {
  HIGH = "high", // Used high accuracy (success)
  LOW = "low", // Used fallback to low accuracy (less precise)
  ERROR = "error", // Complete timeout, used 0,0 coordinates
}

export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  /**
   * Ratio of timeout to use for high accuracy attempt (0-1).
   * Remaining time will be used for low accuracy fallback.
   * Default: 0.6 (60% for high accuracy, 40% for fallback)
   */
  highAccuracyTimeoutRatio?: number;
}

export interface GeolocationResult {
  coordinates: Coordinates;
  accuracy: number;
  timestamp: number;
  accuracyLevel?: GeolocationAccuracyLevel; // Optional to maintain backward compatibility
}

export class GeolocationService {
  private static readonly DEFAULT_TIMEOUT = 30000; // 30 seconds
  private static readonly DEFAULT_MAXIMUM_AGE = 300000; // 5 minutes
  private static readonly DEFAULT_HIGH_ACCURACY_TIMEOUT_RATIO = 0.6; // 60% for high accuracy, 40% for fallback

  /**
   * Check if geolocation is supported by the browser
   */
  static isGeolocationSupported(): boolean {
    return "geolocation" in navigator;
  }

  /**
   * Get current position with configurable options
   * Uses fallback strategy: tries high accuracy first, then falls back to low accuracy on timeout
   */
  static async getCurrentPosition(
    options: GeolocationOptions = {},
  ): Promise<GeolocationResult> {
    if (!this.isGeolocationSupported()) {
      throw new Error(GeolocationError.NOT_SUPPORTED);
    }

    const {
      enableHighAccuracy = true,
      timeout = this.DEFAULT_TIMEOUT,
      maximumAge = this.DEFAULT_MAXIMUM_AGE,
      highAccuracyTimeoutRatio = this.DEFAULT_HIGH_ACCURACY_TIMEOUT_RATIO,
    } = options;

    // Strategy: try high accuracy first, then fallback to lower accuracy on timeout
    if (enableHighAccuracy) {
      const highAccuracyTimeout = Math.floor(
        timeout * highAccuracyTimeoutRatio,
      );
      const lowAccuracyTimeout = timeout - highAccuracyTimeout;

      try {
        const result = await this.getCurrentPositionOnce({
          enableHighAccuracy: true,
          timeout: highAccuracyTimeout,
          maximumAge,
        });
        return { ...result, accuracyLevel: GeolocationAccuracyLevel.HIGH };
      } catch (error) {
        // If timeout, fallback to lower accuracy
        if (
          error instanceof Error &&
          error.message === GeolocationError.TIMEOUT
        ) {
          const result = await this.getCurrentPositionOnce({
            enableHighAccuracy: false,
            timeout: lowAccuracyTimeout,
            maximumAge,
          });
          return { ...result, accuracyLevel: GeolocationAccuracyLevel.LOW };
        }
        throw error;
      }
    }

    // When not using fallback, use low accuracy directly
    const result = await this.getCurrentPositionOnce({
      enableHighAccuracy: false,
      timeout,
      maximumAge,
    });
    return { ...result, accuracyLevel: GeolocationAccuracyLevel.LOW };
  }

  /**
   * Get current position once without retry
   */
  private static async getCurrentPositionOnce(
    options: GeolocationOptions,
  ): Promise<GeolocationResult> {
    const { enableHighAccuracy, timeout, maximumAge } = options;

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates: Coordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          // Validate coordinates using existing schema
          const validationResult = nullableCoordinates.safeParse(coordinates);

          if (!validationResult.success) {
            reject(new Error(GeolocationError.POSITION_UNAVAILABLE));
            return;
          }

          resolve({
            coordinates: validationResult.data,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              reject(new Error(GeolocationError.PERMISSION_DENIED));
              break;
            case error.POSITION_UNAVAILABLE:
              reject(new Error(GeolocationError.POSITION_UNAVAILABLE));
              break;
            case error.TIMEOUT:
              reject(new Error(GeolocationError.TIMEOUT));
              break;
            default:
              reject(new Error(GeolocationError.POSITION_UNAVAILABLE));
          }
        },
        {
          enableHighAccuracy,
          timeout,
          maximumAge,
        },
      );
    });
  }

  /**
   * Get current position coordinates with fallback to zero on timeout.
   * Returns zero coordinates if timeout occurs, throws error for other geolocation errors.
   */
  static async getCurrentPositionWithTimeoutFallback(
    options: GeolocationOptions = {},
  ): Promise<{
    latitude: number | null;
    longitude: number | null;
    accuracyLevel: GeolocationAccuracyLevel;
  }> {
    try {
      const position = await this.getCurrentPosition(options);
      return {
        latitude: position.coordinates.latitude ?? 0,
        longitude: position.coordinates.longitude ?? 0,
        accuracyLevel: position.accuracyLevel ?? GeolocationAccuracyLevel.HIGH,
      };
    } catch (error) {
      // If timeout, return zero coordinates instead of throwing
      if (
        error instanceof Error &&
        error.message === GeolocationError.TIMEOUT
      ) {
        return {
          latitude: null,
          longitude: null,
          accuracyLevel: GeolocationAccuracyLevel.ERROR,
        };
      }
      // For other errors (permission denied, etc.), still throw
      throw error;
    }
  }

  /**
   * Check if user has granted geolocation permissions
   */
  static async checkPermissions(): Promise<PermissionState> {
    if (!("permissions" in navigator)) {
      throw new Error("Permissions API not supported");
    }

    try {
      const permission = await navigator.permissions.query({
        name: "geolocation",
      });
      return permission.state;
    } catch (error) {
      throw new Error("Failed to check geolocation permissions");
    }
  }
}
